import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";

const API_URL = "https://backend.aktcorp.com/media/addmedia";

export const AddNews = () => {
  const [formData, setFormData] = useState({ title: "", description: "", image: null });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [showPage, setShowPage] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPage(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploading(true);
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setTimeout(() => {
          setFormData({ ...formData, image: reader.result.split(",")[1] });
          setUploading(false);
        }, 2000);
      };
    }
  };

  const validateForm = () => {
    let newErrors = {};
    if (!formData.title.trim()) newErrors.title = "* This field is required";
    if (!formData.description.trim()) newErrors.description = "* This field is required";
    if (!formData.image) newErrors.image = "* This field is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    const token = localStorage.getItem("authToken"); // ✅ Get JWT token from localStorage
    if (!token) {
      alert("User not authenticated. Please login.");
      setLoading(false);
      return;
    }

    const payload = {
      title: formData.title,
      description: formData.description,
      imageBase64: formData.image,
      imageType: "jpeg"
    };

    try {
      await axios.post(API_URL, payload, {
        headers: {
          Authorization: `Bearer ${token}`, // ✅ Set JWT token in Authorization header
          "Content-Type": "application/json"
        }
      });
      setTimeout(() => {
        setLoading(false);
        setSuccessMessage("News added successfully!");
        setFormData({ title: "", description: "", image: null });
        if (fileInputRef.current) fileInputRef.current.value = "";
        setTimeout(() => setSuccessMessage(""), 5000);
      }, 3000);
    } catch (error) {
      setLoading(false);
      console.error("Error adding news:", error);
      alert("Failed to add news. Please try again.");
    }
  };

  if (!showPage) {
    return (
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
        className="w-24 h-24 border-4 border-t-transparent border-blue-600 rounded-full mx-auto mt-32"
      ></motion.div>
    );
  }

  return (
    <div className="max-w-lg mx-auto p-6 bg-white shadow-md rounded-lg">
      <motion.h2
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-3xl font-bold text-gray-900 bg-clip-text text-center mb-4 text-transparent bg-gradient-to-r from-blue-500 to-purple-500"
      >
        Add Media
      </motion.h2>
      <form onSubmit={handleSubmit}>
        <p className="font-semibold">Enter Title :</p>
        <input
          type="text"
          placeholder="Title"
          className="border w-full p-2 mb-2 rounded-md"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        />
        {errors.title && <p className="text-red-500 text-sm mb-2">{errors.title}</p>}

        <p className="font-semibold">Enter Description :</p>
        <textarea
          placeholder="Description"
          className="border w-full p-2 mb-2 rounded-md"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        />
        {errors.description && <p className="text-red-500 text-sm mb-2">{errors.description}</p>}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="mb-2"
          onChange={handleImageChange}
        />
        {errors.image && <p className="text-red-500 text-sm mb-2">{errors.image}</p>}

        {uploading && <div className="w-full h-1 bg-red-500 animate-pulse mb-4"></div>}

        {!loading ? (
          <button type="submit" className="w-full bg-blue-500 text-white p-2 mt-2 rounded-md">
            Add News
          </button>
        ) : (
          <div className="flex justify-center py-2">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
      </form>

      {successMessage && (
        <div className="mt-4 text-green-500 text-center">{successMessage}</div>
      )}
    </div>
  );
};

export default AddNews;
